import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, router } from '@inertiajs/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'
import { toast } from 'sonner'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form'
import { Toaster } from '../../components/ui/sonner'
import { Logo } from '../../components/logo'

const resetPasswordSchema = z.object({
  password: z.string().min(8),
  password_confirmation: z.string().min(8),
}).refine((data) => data.password === data.password_confirmation, {
  path: ["password_confirmation"],
})

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>

interface ResetPasswordProps {
  token: string
  flash?: {
    success?: string
    error?: string
  }
}

export default function ResetPassword({ token, flash }: ResetPasswordProps) {
  const { t } = useTranslation('auth')
  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      password_confirmation: '',
    },
  })

  // Show flash messages as toasts
  useEffect(() => {
    if (flash?.success) {
      toast.success(flash.success)
    }
    if (flash?.error) {
      toast.error(flash.error)
    }
  }, [flash])

  const onSubmit = (data: ResetPasswordForm) => {
    router.patch(`/password_resets/${token}`, data)
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Toaster />

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo className="h-12" />
        </div>

        <Card className="w-full shadow-none">
          <CardHeader>
            <CardTitle>{t('resetPassword.title')}</CardTitle>
            <CardDescription>
              {t('resetPassword.subtitle')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('resetPassword.newPassword')}</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder={t('resetPassword.newPasswordPlaceholder')}
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password_confirmation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('resetPassword.confirmPassword')}</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="mt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('resetPassword.rememberPassword')}{' '}
            <Link href="/login" className="text-primary hover:underline font-medium">
              {t('resetPassword.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
